import { useState, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark';

const THEME_STORAGE_KEY = 'uchat-theme-mode';

// 主题颜色类型
export type ThemeColors = {
  background: string;
  text: string;
  textSecondary: string;
  border: string;
  borderSecondary: string;
  hoverBg: string;
  hoverText: string;
  senderBg: string;
  senderBorder: string;
  senderPlaceholder: string;
  messageBg: string;
  evaluationBg: string;
  evaluationBorder: string;
  evaluationText: string;
  evaluationMeta: string;
  evaluationFeedbackBg: string;
  evaluationFeedbackBorder: string;
  evaluationFeedbackText: string;
  evaluationSkippedBg: string;
  evaluationSkippedBorder: string;
  evaluationSkippedText: string;
  shadow: string;
  shadowStrong: string;
  removeButtonBg: string;
  removeButtonBgHover: string;
  imageLoadingBg: string;
  /** V2 主题映射时注入，用于订单卡片等逻辑分支 */
  appearance?: ThemeMode;
};

// 主题颜色配置
export const themeColors: Record<ThemeMode, ThemeColors> = {
  light: {
    background: '#fff',
    text: '#333',
    textSecondary: '#9aa3b2',
    border: '#ececec',
    borderSecondary: '#d9d9d9',
    hoverBg: '#e6f7ff',
    hoverText: '#1890ff',
    senderBg: '#fff',
    senderBorder: '#ececec',
    senderPlaceholder: '#9aa3b2',
    messageBg: '#fff',
    evaluationBg: '#f6ffed',
    evaluationBorder: '#d9f7be',
    evaluationText: '#52c41a',
    evaluationMeta: '#1890ff',
    evaluationFeedbackBg: 'rgba(255, 255, 255, 0.8)',
    evaluationFeedbackBorder: '#e8f4fd',
    evaluationFeedbackText: '#262626',
    evaluationSkippedBg: '#fafafa',
    evaluationSkippedBorder: '#d9d9d9',
    evaluationSkippedText: '#8c8c8c',
    shadow: 'rgba(0, 0, 0, 0.04)',
    shadowStrong: 'rgba(0, 0, 0, 0.1)',
    removeButtonBg: 'rgba(0, 0, 0, 0.45)',
    removeButtonBgHover: 'rgba(0, 0, 0, 0.65)',
    imageLoadingBg: 'rgba(255, 255, 255, 0.8)',
  },
  dark: {
    background: '#14172B',
    text: '#ffffff',
    textSecondary: '#9aa3b2',
    border: '#2a2f47',
    borderSecondary: '#3a3f57',
    hoverBg: '#1e2338',
    hoverText: '#4a9eff',
    senderBg: '#14172B',
    senderBorder: '#2a2f47',
    senderPlaceholder: '#6b7280',
    messageBg: '#1e2338', // 卡片背景，比主背景稍亮，深灰色
    evaluationBg: '#353847', // 暗色模式下评价消息背景色
    evaluationBorder: '#2d4a2d',
    evaluationText: '#6bcf6b',
    evaluationMeta: '#4a9eff',
    evaluationFeedbackBg: 'rgba(20, 23, 43, 0.8)',
    evaluationFeedbackBorder: '#2a3f4a',
    evaluationFeedbackText: '#e5e5e5',
    evaluationSkippedBg: '#1a1d2e',
    evaluationSkippedBorder: '#2a2f47',
    evaluationSkippedText: '#9aa3b2',
    shadow: 'rgba(0, 0, 0, 0.5)',
    shadowStrong: 'rgba(0, 0, 0, 0.7)',
    removeButtonBg: 'rgba(255, 255, 255, 0.2)',
    removeButtonBgHover: 'rgba(255, 255, 255, 0.3)',
    imageLoadingBg: 'rgba(20, 23, 43, 0.9)',
  },
};

export const useTheme = () => {
  const [theme, setTheme] = useState<ThemeMode>(() => {
    // 从 localStorage 读取保存的主题，默认为 light
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as ThemeMode;
    return savedTheme || 'light';
  });

  const toggleTheme = useCallback(() => {
    setTheme((prev) => {
      const newTheme = prev === 'light' ? 'dark' : 'light';
      localStorage.setItem(THEME_STORAGE_KEY, newTheme);
      return newTheme;
    });
  }, []);

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setTheme(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, []);

  // 获取当前主题的颜色配置
  const colors = themeColors[theme];

  return {
    theme,
    colors,
    toggleTheme,
    setThemeMode,
    isDark: theme === 'dark',
  };
};

